import { BaseClient } from '../BaseClient';
import type {
  CreateTicketRequest,
  CreateTicketResponse,
  CreateTicketWithTokenRequest,
  GetGroupsRequest,
  GetGroupsResponse,
  GetPrioritiesRequest,
  GetPrioritiesResponse,
  GetStatesRequest,
  GetStatesResponse,
  HealthCheckRequest,
  HealthCheckResponse,
} from '../compiled-protos/zammad';
import { ZammadServiceClient } from '../compiled-protos/zammad.client';

export class ZammadClient extends BaseClient {
  private client: ZammadServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.client = new ZammadServiceClient(this.transport);
  }

  /**
   * Create a new support ticket in Zammad
   */
  async createTicket(request: CreateTicketRequest): Promise<CreateTicketResponse> {
    console.log('🎫 ZammadClient.createTicket called:', {
      host: this.host,
      request: {
        title: request.title,
        group: request.group,
        priority: request.priority,
        state: request.state,
      },
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.createTicket(request, this.getMetaData());
      console.log('✅ ZammadClient.createTicket response:', {
        ticketId: response.response.ticketId,
        ticketNumber: response.response.ticketNumber,
        success: response.response.success,
      });
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('❌ ZammadClient.createTicket error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Create a new support ticket in Zammad with token authentication
   * Used by mobile app for authenticated ticket creation
   */
  async createTicketWithToken(request: CreateTicketWithTokenRequest): Promise<CreateTicketResponse> {
    console.log('🎫 ZammadClient.createTicketWithToken called:', {
      host: this.host,
      request: {
        title: request.title,
        group: request.group,
        priority: request.priority,
        hasToken: !!request.token,
      },
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.createTicketWithToken(request, this.getMetaData());
      console.log('✅ ZammadClient.createTicketWithToken response:', {
        ticketId: response.response.ticketId,
        ticketNumber: response.response.ticketNumber,
        success: response.response.success,
      });
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('❌ ZammadClient.createTicketWithToken error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Get available groups for ticket assignment
   */
  async getGroups(request: GetGroupsRequest): Promise<GetGroupsResponse> {
    console.log('📋 ZammadClient.getGroups called:', {
      host: this.host,
      activeOnly: request.activeOnly,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getGroups(request, this.getMetaData());
      console.log('✅ ZammadClient.getGroups response:', {
        groupCount: response.response.groups.length,
        success: response.response.success,
      });
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('❌ ZammadClient.getGroups error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Get available ticket priorities
   */
  async getPriorities(request: GetPrioritiesRequest): Promise<GetPrioritiesResponse> {
    console.log('🔢 ZammadClient.getPriorities called:', {
      host: this.host,
      activeOnly: request.activeOnly,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getPriorities(request, this.getMetaData());
      console.log('✅ ZammadClient.getPriorities response:', {
        priorityCount: response.response.priorities.length,
        success: response.response.success,
      });
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('❌ ZammadClient.getPriorities error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Get available ticket states
   */
  async getStates(request: GetStatesRequest): Promise<GetStatesResponse> {
    console.log('📊 ZammadClient.getStates called:', {
      host: this.host,
      activeOnly: request.activeOnly,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.getStates(request, this.getMetaData());
      console.log('✅ ZammadClient.getStates response:', {
        stateCount: response.response.states.length,
        success: response.response.success,
      });
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('❌ ZammadClient.getStates error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  /**
   * Health check for Zammad connectivity
   */
  async healthCheck(request: HealthCheckRequest = {}): Promise<HealthCheckResponse> {
    console.log('🏥 ZammadClient.healthCheck called:', {
      host: this.host,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await this.client.healthCheck(request, this.getMetaData());
      console.log('✅ ZammadClient.healthCheck response:', {
        status: response.response.status,
        version: response.response.version,
        zammadReachable: response.response.zammadReachable,
      });
      return response.response;
    } catch (error) {
      const err = error as any;
      console.error('❌ ZammadClient.healthCheck error:', {
        error,
        errorMessage: err.message,
        errorCode: err.code,
        errorDetails: err.details,
        host: this.host,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
